import { useState, useRef, useEffect } from 'react';
import {ALL, FACEBOOK, INSTAGRAM,
  PLATFORM_LIST,
  UPGRADE_SUBSCRIPTION_URL
} from '../../../../config/constants';
import {
  ALL_PLATFORM_ICONS,
  PLATFORM_COLORS,
  PLATFORM_ICONS
} from '../../../../config/icons';
import SelectPlatformToggle from '../togglePlatformButton';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from '@tolgee/react';
import PostTypeDropDown from '../postTypeDropDown';
import {
  CROP_RATIO_9_16,
  CROP_RATIOS,
  platformFeatures,
  POST_TYPE_KEY,
  POST_TYPE_POST_KEY,
  POST_TYPE_REEL_KEY,
  POST_TYPE_TRIAL_REEL_KEY,
  POST_TYPE_STORY_KEY,
  POST_TYPE_TEMPLATE,
  showPostTypeSelector
} from '../../config/platformFeatures';
import { Dropdown } from 'react-bootstrap';
import { SHOWM_MAX_PLATFORMS_COUNT } from '../contants';
import {
  getObjectAsFile,
  getPostTypesList,
  getSmartCrop
} from '../../utils/helpers';
import { useClickAway } from 'react-use';
import {
  addCropObject,
  setGeneralPostType,
  setShowPostBoost,
  updateCampaignList,
  updatePlatformEnabled,
  updatePostTypeSelected
} from '../../../../redux/slices/lomavisCreator/lomavisCreator';
import CreatorTitle from '../commonElements/creatorTitle';
import CreatorCard from '../commonElements/creatorCard';
import PostStatusBadge from '../postStatusBadge';

import { isValidPostBoostConfig } from '../../../../utils/postBoostValidation';
import { lomavisShowToastr } from '../../../../utils/ui/toastr';
import UpgradeRequiredTooltipWrapper from '../../../utils/billing/UpgradeRequiredTooltipWrapper';
import { LomavisTagListAndSelector1 } from '@/lomavisSharedUi/organisms/inputs/LomavisTagListAndSelector1';
import { resolve } from '@/api/endPoints';
import axiosClient from '@/utils/axios';
import { CampaignType } from '@/redux/slices/lomavisCreator/lomavisCreatorConfig';
import UpgradeRequiredTooltipWrapper1 from '@/lomavisSharedUi/atoms/billing/UpgradeRequiredTooltipWrapper1/UpgradeRequiredTooltipWrapper1';
import { validateUpload } from '../../config/postValidator';
import toast from 'react-hot-toast';
import { STORY } from '../../config/constants';

const PlatformsSelection: React.FC = () => {
  const [editSelectedPlatforms, setEditSelectedPlatforms] = useState(false);
  const [allCampaigns, setAllCampaigns] = useState<CampaignType[]>([]);
  const [loadingCampaign, setLoadingCampaign] = useState(false);

  const ref = useRef(null); // This is used to set relative positioning context
  const urlParams = new URLSearchParams(window.location.search);
  const preset = urlParams.get('preset');

  const {
    lomavisCreatorConfig: { connected_platforms, supported_platforms },
    platformEnabled,
    postTypeSelected,
    locationUuid,
    general_post_type,
    cloudUuid,
    lomavisCreatorConfig,
    postData,
    postBoost: {
      boost_config,
      ui: { sameBoostForAll }
    },
    campaigns,
    postCampaign,
    instagram_direct_connect
  } = useSelector((state: any) => state.lomavisCreator);

  const [showPostTypeDropdown, setShowPostTypeDropdown] = useState(false);
  const handleClick = () => {
    setEditSelectedPlatforms(!editSelectedPlatforms);
  };
  //useClickaway hook
  const platformSelectionRef = useRef(null);
  useClickAway(platformSelectionRef, () => {
    setEditSelectedPlatforms(false);
  });
  const postTypeRef = useRef(null);
  //useClickaway hook
  useClickAway(postTypeRef, () => {
    setShowPostTypeDropdown(false);
  });
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const resolveConcreteType = (platform, maybeGroupType) => {
    // If a group key (e.g. POST_TYPE_KEY) was passed, normalize to the platform’s default option
    if (
      maybeGroupType !== POST_TYPE_REEL_KEY &&
      maybeGroupType !== POST_TYPE_TRIAL_REEL_KEY &&
      maybeGroupType !== POST_TYPE_POST_KEY
    ) {
      return platformFeatures[platform]?.[POST_TYPE_KEY]?.[0]?.typeKey ?? null;
    }
    return maybeGroupType;
  };

  const platformSupportsType = (platform, concreteType) => {
    if (!concreteType) return false;
    return Boolean(
      platformFeatures[platform]?.[concreteType] ||
        platformFeatures[platform]?.[POST_TYPE_KEY]?.some(
          (obj) => obj.typeKey === concreteType
        )
    );
  };

  const getFiles = (platform) =>
    (postData?.[platform]?.media ?? []).map((item) =>
      item?.file ? item.file : getObjectAsFile(item)
    );

  const updateGeneralPostTypeSelected = async (postType) => {
    const switchToTemplateOrTemplateSelected =
      postType === POST_TYPE_TEMPLATE ||
      postTypeSelected === POST_TYPE_TEMPLATE;
    for (const platform of Object.keys(platformEnabled)) {
      if (
        postData[platform]?.media.length > 0 &&
        switchToTemplateOrTemplateSelected
      ) {
        lomavisShowToastr(
          t('lomavisCreator.errors.postTypeMediaAvailable'),
          'error'
        );
        return;
      }
    }

    const candidates = Object.keys(platformEnabled).filter(
      (p) =>
        platformEnabled[p] && connected_platforms[p] && supported_platforms[p]
    );

    const hasAnyMedia = candidates.some(
      (p) => (postData[p]?.media?.length ?? 0) > 0
    );

    if (hasAnyMedia && postType) {
      const candidates = Object.keys(platformEnabled).filter(
        (p) =>
          platformEnabled[p] && connected_platforms[p] && supported_platforms[p]
      );

      // must have at least one candidate that both supports AND validates with current media
      let anyPasses = false;

      for (const p of candidates) {
        let platformType = resolveConcreteType(p, postType);

        if (!platformSupportsType(p, postType)) continue;
        try {
          const ok = await validateUpload(
            p,
            platformType,
            getFiles(p),
            t,
            false
          );

          if (ok) {
            anyPasses = true;
            break;
          }
        } catch (e) {
          console.error('Validation error (preflight):', e?.message ?? e);
          // treat as a failure and continue checking others
        }
      }

      if (!anyPasses) {
        // Do NOT change general post type; bail early.
        lomavisShowToastr(
          t(
            'lomavisCreator.errors.postTypeBreaksAllRulesForPlatforms',
            'Selected post type is invalid for current media on all platforms.'
          ),
          'error'
        );
        return;
      }
    }

    // 2) Either no media (skip checks) OR preflight passed → proceed as usual
    dispatch(setGeneralPostType(postType));

    let autoDisable = false;
    Object.keys(platformEnabled).map((platform) => {
      // check if platform is connected and supported
      if (!connected_platforms[platform] || !supported_platforms[platform])
        return null;

      if (
        !(
          platformFeatures[platform]?.[postType] ||
          platformFeatures[platform]?.[POST_TYPE_KEY].some(
            (obj) => obj.typeKey === postType
          )
        )
      ) {
        if (platformEnabled[platform]) {
          dispatch(updatePlatformEnabled({ platform, enabled: false }));
          autoDisable = true;
        }
      } else {
        dispatch(updatePlatformEnabled({ platform, enabled: true }));
      }

      //update the platform individual posttype

      const updatePlatformPostType = async () => {
        const is_valid = async () => {
          try {
            const validationState = validateUpload(
              platform,
              postType,
              postData?.[platform]?.media.map((item) =>
                item?.file ? item?.file : getObjectAsFile(item)
              ),
              t
            );

            return validationState;
          } catch (error) {
            console.error('Validation error:', error.message);
            return false; // or you could return an error object, or handle it however you need to
          }
        };

        if (await is_valid()) {
          dispatch(
            updatePostTypeSelected({
              platform,
              postType: postType
            })
          );
        }
      };

      if (
        platformFeatures[platform]?.[POST_TYPE_KEY].some(
          (obj) => obj.typeKey === postType
        )
      ) {
        updatePlatformPostType();
      } else if (
        postType !== POST_TYPE_REEL_KEY &&
        postType !== POST_TYPE_TRIAL_REEL_KEY &&
        postType !== POST_TYPE_STORY_KEY
      ) {
        dispatch(
          updatePostTypeSelected({
            platform,
            postType: platformFeatures[platform][POST_TYPE_KEY][0].typeKey
          })
        );
      }
    });
    if (autoDisable) {
      lomavisShowToastr(
        t('lomavisCreator.postTypeNotSupportedForAllSelectedPlatforms'),
        'success'
      );
    }
  };

  /*
   * Returns the translated string for the post type label taking into account different labels for different
   * areas where the post can be used.
   * */
  const getPostTypeLabelString = (postType) => {
    let key = `lomavisCreator.postType.${postType}`;
    return key;
  };

  /*
   * Returns if a post type selector should be clickable or not for a given platform.
   * */
  const isPostTypeClickable = (postType) => {
    if (postType !== POST_TYPE_TEMPLATE) {
      return true;
    }

    if (lomavisCreatorConfig.is_multipost) {
      return lomavisCreatorConfig.features.multipost_templates_posts;
    }

    return true;
  };

  const getCampaignList = async () => {
    setLoadingCampaign(true);

    try {
      let url = resolve('campaign_list', {}, { location__uuid: locationUuid });
      let allResults: CampaignType[] = [];

      while (url) {
        const res = await axiosClient.get(url);
        const data = res.data;

        allResults = [...allResults, ...data.results];

        // Move to the next page if available
        url = data.next;
      }

      setAllCampaigns(allResults);
    } catch (err: any) {
      console.error('Error fetching campaigns:', err);
      // setError("Failed to fetch campaigns");
    } finally {
      setLoadingCampaign(false);
    }
  };
  const createCampaign = async (name: string): Promise<CampaignType | null> => {
    try {
      let url = resolve('campaign_list', {}, {});
      const payload = {
        name,
        location_uuid: locationUuid
      };

      const res = await axiosClient.post(url, payload);
      const newCampaign: CampaignType = res.data;

      // Optionally update local state if you're storing campaigns
      setAllCampaigns((prev) => [newCampaign, ...prev]);
      lomavisShowToastr(t('lomavisCreator.campaignAdded'), 'success');

      return newCampaign;
    } catch (err: any) {
      console.error('Error creating campaign:', err);
      return null;
    }
  };
  const deleteCampaign = async (uuid: string): Promise<boolean> => {
    try {
      const url = resolve('campaign_list_detail', { uuid }, {});

      await axiosClient.delete(url);

      // Remove it from local state
      setAllCampaigns((prev) => prev.filter((c) => c.uuid !== uuid));

      return true;
    } catch (err: any) {
      console.error('Error deleting campaign:', err);
      return false;
    }
  };

  useEffect(() => {
    if (postCampaign) {
      getCampaignList();
    }
  }, [postCampaign]);

  const handlePostBoostClick = () => {
    // Check if Facebook or Instagram is connected
    if (!connected_platforms[FACEBOOK] && !connected_platforms[INSTAGRAM]) {
      lomavisShowToastr(
        t('lomavisCreator.errors.facebookOrInstagramRequired'),
        'error'
      );
      return;
    }
    const isInstagramOnlyConnected =
      connected_platforms[INSTAGRAM] && !connected_platforms[FACEBOOK];

    if (instagram_direct_connect && isInstagramOnlyConnected) {
      lomavisShowToastr(t('lomavisCreator.IG_direct_connect.error'), 'error');
      return;
    }
    // Check if Meta Ad account is connected
    const metaAdsData = lomavisCreatorConfig?.meta_ads_data;
    const isMetaAdAccountConnected = metaAdsData?.ad_account_connected || false;

    if (!isMetaAdAccountConnected) {
      lomavisShowToastr(
        t('lomavisCreator.postBoost.metaAdAccountRequired'),
        'error'
      );
      return;
    }

    dispatch(setShowPostBoost(true));
  };

  useEffect(() => {
    if (preset === STORY) {
      updateGeneralPostTypeSelected(POST_TYPE_STORY_KEY);

      setTimeout(() => {
        updateGeneralPostTypeSelected(POST_TYPE_STORY_KEY);
      }, 2000);
    }
  }, [preset]);
  const hasRun = useRef(false);
  useEffect(() => {
    if (preset !== STORY) return;

    if (hasRun.current) return; // ⛔ already executed once
    if (!postData) return; // ⛔ nothing loaded yet

    // ✅ check if at least one platform has media
    const hasMediaLoaded = [...Object.keys(platformEnabled), ALL].some(
      (platform) => {
        return (postData?.[platform]?.media || []).length > 0;
      }
    );

    if (!hasMediaLoaded) return; // ⛔ still waiting for media to load

    hasRun.current = true; // ✅ mark as executed

    // ---- Your original logic ----
    [...Object.keys(platformEnabled), ALL].forEach((platform) => {
      const platformMedia = postData?.[platform]?.media || [];
      platformMedia.forEach((item) => {
        (
          getSmartCrop(
            item?.original_asset,
            CROP_RATIOS[CROP_RATIO_9_16]?.width,
            CROP_RATIOS[CROP_RATIO_9_16]?.height
          ) as Promise<{ x: number; y: number; width: number; height: number }>
        )
          .then((crop) => {
            dispatch(
              addCropObject({
                platform,
                cropInfo: {
                  width: crop.width,
                  height: crop.height,
                  x: crop.x,
                  y: crop.y,
                  uuid: item?.uuid,
                  aspect_ratio_width: CROP_RATIOS[CROP_RATIO_9_16]?.width,
                  aspect_ratio_height: CROP_RATIOS[CROP_RATIO_9_16]?.height
                }
              })
            );
          })
          .catch((error) => {
            console.error(`Smart crop failed for ${platform}`, error);
          });
      });
    });
  }, [postData, preset]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <CreatorCard>
        <PostStatusBadge />
        <div className="d-flex flex-column justify-content-between align-items-center ">
          <div className="d-flex justify-content-between align-items-center w-100">
            <CreatorTitle title="platforms" tooltipPosition="right" />
            <div className="d-flex align-items-center w-100 justify-content-end ">
              <Dropdown
                className=" d-flex justify-content-end"
                show={showPostTypeDropdown}
                onClick={() => setShowPostTypeDropdown(!showPostTypeDropdown)}
                ref={postTypeRef}
              >
                <Dropdown.Toggle
                  variant="secondary"
                  id="dropdown-basic"
                  size="sm"
                >
                  {' '}
                  {general_post_type
                    ? t(getPostTypeLabelString(general_post_type))
                    : t(getPostTypeLabelString(POST_TYPE_KEY))}
                </Dropdown.Toggle>
                <Dropdown.Menu className="autoScaleDropDownMenu w-250px">
                  {getPostTypesList({
                    locationUuid,
                    cloudUuid,
                    lomavisCreatorConfig
                  }).map((postType, index) => (
                    <div key={index}>
                      <UpgradeRequiredTooltipWrapper
                        show={!isPostTypeClickable(postType)}
                        children={
                          <span className={'display-block'}>
                            <Dropdown.Item
                              key={index}
                              id={postType}
                              onClick={() =>
                                updateGeneralPostTypeSelected(postType)
                              }
                              className={
                                !isPostTypeClickable(postType) ? 'disabled' : ''
                              }
                            >
                              <div className="d-flex flex-column" key={index}>
                                <div className="fw-bolder">
                                  {t(getPostTypeLabelString(postType))}
                                </div>
                                <div
                                  className="fs-7 fw-semibold text-muted"
                                  key={index}
                                  style={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {t(
                                    `lomavisCreator.postType.${postType}Description`
                                  )}
                                </div>
                              </div>
                            </Dropdown.Item>
                          </span>
                        }
                      />

                      {index <
                        getPostTypesList({ locationUuid, cloudUuid }).length -
                          1 && (
                        <div className="separator separator-dashed my-3" />
                      )}
                    </div>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-center w-100 justify-content-between">
            <div ref={platformSelectionRef}>
              <div
                className="d-flex flex-row flex-no-wrap cursor-pointer align-items-center text-select-none"
                onClick={handleClick}
              >
                {Object.keys(platformEnabled)
                  .filter((item) => platformEnabled[item])
                  .slice(0, SHOWM_MAX_PLATFORMS_COUNT)
                  .map((platform) => (
                    <i
                      key={`icon-${platform}`}
                      className={ALL_PLATFORM_ICONS[platform] + ' fs-2x me-4'}
                      style={{ color: PLATFORM_COLORS[platform] }}
                    ></i>
                  ))}

                <PlusIcon
                  count={
                    Object.keys(platformEnabled).filter(
                      (item) => platformEnabled[item]
                    ).length - SHOWM_MAX_PLATFORMS_COUNT
                  }
                />
                <i
                  className="fa-duotone fa-pen fs-1x mb-n10 ms-n2"
                  onClick={handleClick}
                ></i>
              </div>
              {editSelectedPlatforms && (
                <div
                  className="card shadow m-1 p-3"
                  style={{
                    position: 'absolute',
                    zIndex: 9999 // Ensure the card appears on top of other content
                  }}
                >
                  <div>
                    <span className="fw-bold text-dark  mb-2 user-select-none">
                      {t('lomavisCreator.update_platforms')}
                    </span>
                  </div>

                  <div className="d-flex flex-row flex-wrap">
                    {PLATFORM_LIST.map(
                      (platform) =>
                        connected_platforms[platform] &&
                        supported_platforms[platform] && (
                          <div key={platform} className="p-2">
                            <SelectPlatformToggle platform={platform} />
                          </div>
                        )
                    )}
                  </div>
                  <div className="d-flex justify-content-end mt-2">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={handleClick}
                    >
                      {t('lomavisCreator.close')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {locationUuid && (
              <div className="d-flex align-items-end flex-grow-1 w-auto justify-content-end mt-2">
                <UpgradeRequiredTooltipWrapper1 show={!postCampaign}>
                  <LomavisTagListAndSelector1
                    availableTags={allCampaigns}
                    initialSelected={campaigns}
                    maxVisible={5}
                    maxAllowedTags={5}
                    deleteTag={deleteCampaign}
                    onCreateTag={(tagName) => createCampaign(tagName)}
                    onTagsChange={(tags) => {
                      dispatch(updateCampaignList({ campaigns: tags }));
                    }}
                    direction={'left'}
                    addTagsText={t('lomavisCreator.addCampaign')}
                  />
                </UpgradeRequiredTooltipWrapper1>
              </div>
            )}
          </div>
        </div>
        <div className="d-flex align-items-center mt-4 mb-2">
          <hr
            className="w-100 text-muted"
            style={{ borderTop: '1px dashed' }}
          />
        </div>
        <div className="d-flex justify-content-between">
        <div className="d-flex">
            {Object.keys(platformEnabled)
              .filter((item) => platformEnabled[item])
              .map((platform) => {
                if (
                  !platformEnabled[platform] ||
                  !showPostTypeSelector(platform)
                )
                  return null;
                return (
                  <div
                    key={platform}
                    className="d-flex align-items-center me-2"
                  >
                    <i
                      className={PLATFORM_ICONS[platform] + ' me-2'}
                      style={{ color: PLATFORM_COLORS[platform], fontSize: 20 }}
                    ></i>

                    <PostTypeDropDown platform={platform} />
                  </div>
                );
              })}
          </div>
          {!cloudUuid && !lomavisCreatorConfig.is_multipost && (
            <button
              className={`btn btn-secondary btn-sm ${
                isValidPostBoostConfig(
                  boost_config,
                  platformEnabled,
                  t,
                  sameBoostForAll
                )
                  ? 'border border-success border-2'
                  : ''
              }`}
              onClick={handlePostBoostClick}
            >
              <i className="far fa-rectangle-ad me-2"></i>
              {t('lomavisCreator.boost_post')}
            </button>
          )}
        </div>
      </CreatorCard>
    </div>
  );
};

export default PlatformsSelection;

const PlusIcon: React.FC<{ count: number }> = ({ count }) => {
  if (count <= 0) return null;
  return (
    <div
      className="d-flex justify-content-center align-items-center fs-1x fw-bold me-2 bg-dark text-white p-4"
      style={{
        width: '35px',
        height: '35px',
        borderRadius: 10
      }}
    >
      <span>+{count}</span>
    </div>
  );
};
